import { SubscribeApi } from 'api/subscribeApi';
import { Dropdown, SelectOption } from 'components/dropdown/Dropdown';
import { ErrorM } from 'components/formComponents/ErrorM';
import { Spinner } from 'components/spinner/Spinner';
import { Form, Formik } from 'formik';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setMembershipOptInfo } from 'reducers/misc';
import * as Yup from 'yup';

type MembershipOptInfoFormProps = {
  onSubmit: () => void;
};

type VATAddressDetails = {
  VATSchoolName: string;
  VATSchoolAddress: string;
};

export const MembershipOptInfoForm: React.FC<MembershipOptInfoFormProps> = ({
  onSubmit,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMsg, setErrMsg] = useState<boolean>(false);
  const [vatLoading, setVatLoading] = useState<boolean>(false);
  const [savedVATCode, setSavedVATCode] = useState<string>('');
  const [vatAddressDetails, setVatAddressDetails] =
    useState<VATAddressDetails | null>(null);
  const [initialDetails, setInitialDetails] = useState<any>({
    locationArr: [],
    ageRangeArr: [],
    subjectArr: [],
    selectedLocation: null,
    selectedLocationLabel: '',
    selectedAgeRange: null,
    selectedSubject: [],
    pupilsOnRoll: 0,
    euCountries: [],
    otherCountries: [],    
    freeTrialVatCode: '',
    isVatVerified: false,
  });

  const validationSchema = Yup.object({
    selectedLocation: Yup.object()
      .required('Please select a location')
      .nullable(),
    selectedAgeRange: Yup.object()
      .required('Please select an age range')
      .nullable(),
    selectedSubject: Yup.object()
      .required('Please select at least one subject')
      .nullable(),
    pupilsOnRoll: Yup.number()
      .min(1, 'Please select a number of pupils on roll')
      .required('Please select a number of pupils on roll')
      .nullable(),
    freeTrialVatCode: Yup.string()
      .nullable()
      .test(
        'is-vat-verified',
        'Please enter a valid VAT number',
        function (value) {
          const { selectedLocationLabel, isVatVerified } = this.parent;
          const isNonUkEu =
            selectedLocationLabel &&
            selectedLocationLabel.toLowerCase() === 'non-uk eu';

          if (isNonUkEu && value && !isVatVerified) {
            return false;
          }
          return true;
        }
      ),
  }).test({
    name: 'form validation eu country',
    test: function (
      this: Yup.TestContext,
      value: any
    ): boolean | Yup.ValidationError {
      const euCountrySelected =
        value && value.hasOwnProperty('selectedEUCountry')
          ? value.selectedEUCountry
          : null;

      if (
        (value.selectedLocationLabel && value.selectedLocationLabel.toLowerCase() === 'non-uk eu') &&
        !euCountrySelected
      ) {
        return this.createError({
          path: 'euCountries',
          message: 'Please select a country',
        });        
      } else {
        return true;
      }
    },
  }).test({
    name: 'form validation other country',
    test: function (
      this: Yup.TestContext,
      value: any
    ): boolean | Yup.ValidationError {
      const otherCountrySelected =
        value && value.hasOwnProperty('selectedOtherCountry')
          ? value.selectedOtherCountry
          : null;

      if (
        (value.selectedLocationLabel && value.selectedLocationLabel.toLowerCase() === 'other') &&
        !otherCountrySelected
      ) {
        return this.createError({
          path: 'otherCountries',
          message: 'Please select a country',
        });        
      } else {
        return true;
      }
    },
  });

  const dispatch = useDispatch();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const result = await SubscribeApi.getFreeTrialQuestionsInfo();

    if (result.data) {
      let multiSubArrIndex = findArrIndex(result.data.subjects, 'name', '&');
      result.data.subjects.push(
        result.data.subjects.splice(multiSubArrIndex, 1)[0]
      );

      const euCountriesList = result.data.euCountries.map((country_item: any) => {
        return { label: country_item.name, value: country_item.id };
      });

      const otherCountriesList = result.data.otherCountries.map((country_item: any) => {
        return { label: country_item.name, value: country_item.id };
      });      

      const trialPackageInfo = {
        locationArr:
          result.data.locationTypes.length > 0
            ? result.data.locationTypes.map((item: any) => {
                return { label: item.name, value: item.id };
              })
            : [],
        ageRangeArr:
          result.data.generalPackages.length > 0
            ? result.data.generalPackages.map((item: any) => {
                return {
                  label: item.ageRange + ' years old (' + item.name + ')',
                  value: item.id,
                };
              })
            : [],
        subjectArr:
          result.data.subjects.length > 0
            ? result.data.subjects.map((item: any) => {
                return { label: item.name, value: JSON.parse(item.idsString) };
              })
            : [],
        selectedLocation: null,
        selectedLocationLabel: '',
        selectedAgeRange: null,
        selectedSubject: null,
        unitedKingdom: result.data.unitedKingdom,
        pupilsOnRoll: 0,
        euCountries: euCountriesList,
        otherCountries: otherCountriesList,
      };

      setInitialDetails(trialPackageInfo);
      setLoading(false);
    }

    if (result.error) {
      setErrMsg(true);
    }
  };

  const findArrIndex = (array: any, attr: string, value: string) => {
    for (var i = 0; i < array.length; i += 1) {
      if (array[i][attr].indexOf(value) > -1) {
        return i;
      }
    }
    return -1;
  };

  const validateVATCode = async (
    values: any,
    setFieldError: any,
    setFieldValue: any
  ) => {
    setFieldError('freeTrialVatCode', undefined);
    setFieldValue('isVatVerified', false);
    setVatLoading(true);

    if (values.selectedLocationLabel.toLowerCase() === 'non-uk eu' && !values.selectedEUCountry) {
      setFieldError('freeTrialVatCode', 'Please select a country.');
      setVatLoading(false);
      return;
    }

    if (!values.freeTrialVatCode) {
      setFieldError('freeTrialVatCode', 'Please enter a VAT number.');
      setVatLoading(false);
      return;
    }

    try {
      const result = await SubscribeApi.validateVatNumber(
        values.freeTrialVatCode,
        values.selectedEUCountry.value
      );

      if (
        !result.data &&
        result.error
      ) {
        setFieldError('freeTrialVatCode', result.error.message);
        return;
      } else if (
        result.data &&
        !result.data.validVatNumber
      ){
        setFieldError('freeTrialVatCode', "Invalid Vat Number.");
      } else if (
        result.data &&
        result.data.validVatNumber
      ){
        setFieldValue('isVatVerified', true);
        setFieldError('freeTrialVatCode', undefined);  

        setVatAddressDetails({
          VATSchoolName: result.data.companyName,
          VATSchoolAddress: result.data.companyAddress,
        });      
      }
    } catch (err) {
      setFieldError('freeTrialVatCode', 'Sorry, something went wrong. Please try again.');
    } finally {
      setVatLoading(false);
    }
  };

  const clearVATCode = (setFieldValue: any) => {
    setSavedVATCode('');
    setVatAddressDetails(null);
    setFieldValue('freeTrialVatCode', '');
    setFieldValue('isVatVerified', false);
  };

  const handleSubmit = (values: any, helpers: any) => {
    dispatch(
      setMembershipOptInfo([
        {
          location: values.selectedLocation,
          selectedLocationLabel: values.selectedLocationLabel,
          ageRange: values.selectedAgeRange,
          subject: values.selectedSubject,
          pupilsOnRoll: values.pupilsOnRoll,
          freeTrialVatCode: values.freeTrialVatCode,
          vatNumber: values.freeTrialVatCode || null,
          vatCountry: 
            values.selectedLocationLabel.toLowerCase() === "non-uk eu" ?
            values.selectedEUCountry.value :
            values.selectedLocationLabel.toLowerCase() === "other" ?
            values.selectedOtherCountry.value :
            values.unitedKingdom[0].id
        },
      ])
    );

    onSubmit();
  };

  return (
    <div className="MembershipOptInfoForm">
      <Formik
        initialValues={initialDetails}
        initialErrors={{}}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
        validateOnMount={false}
        enableReinitialize={true}
      >
        {(formik) => {
          return (
            <Form>
              <div className="Form MOInfoForm">
                {loading ? (
                  <Spinner />
                ) : (
                  <>
                    {errorMsg ? (
                      <h2 className="ErrorTxt">
                        AN ERROR OCCURRED, PLEASE TRY AGAIN.
                      </h2>
                    ) : (
                      <>
                        {formik.values.locationArr &&
                          formik.values.locationArr.length > 0 && (
                            <>
                              <div
                                className={
                                  'FormBox' +
                                  (formik.errors.selectedLocation
                                    ? ' ErrorField'
                                    : '')
                                }
                              >
                                <div className="FormLabel">
                                  <label htmlFor="locationArr">Location</label>
                                </div>
                                <div className="FormField">
                                  <Dropdown
                                    id={'locationArr'}
                                    options={formik.values.locationArr}
                                    value={formik.values.selectedLocation}
                                    onChange={(e: SelectOption) => {
                                      formik.setFieldValue(
                                        'selectedLocation',
                                        e
                                      );
                                      formik.setFieldValue(
                                        'selectedLocationLabel',
                                        e.label
                                      );
                                    }}
                                    customClassName="EditSingleSelect"
                                    customNoOptionsMessage="No options"
                                    placeholder={'Please select'}
                                    roundedRectangle={true}
                                  />
                                  <ErrorM name={'selectedLocation'} />
                                </div>
                              </div>

                              {(formik.values.selectedLocationLabel.toLowerCase() === 'non-uk eu') && (
                                <>
                                  <div
                                    className={
                                      'FormBox CountryListBlock' +
                                      (formik.errors.euCountries
                                        ? ' ErrorFB'
                                        : '')
                                    }
                                  >
                                    <div className="FormLabel">
                                      <label htmlFor="CountryListEU">
                                        Country
                                      </label>
                                    </div>
                                    <div className="FormField">
                                      <Dropdown
                                        id={'CountryListEU'}
                                        options={formik.values.euCountries}
                                        value={formik.values.selectedEUCountry}
                                        onChange={(e: SelectOption) =>
                                          formik.setFieldValue(
                                            'selectedEUCountry',
                                            e
                                          )
                                        }
                                        customClassName="EditSingleSelect"
                                        customNoOptionsMessage="No options"
                                        placeholder={'Please select'}
                                        roundedRectangle={true}
                                      />
                                      {formik.errors.euCountries && (
                                        <p className="ErrorTxt">
                                          {(formik as any).errors.euCountries}
                                        </p>
                                      )}
                                    </div>
                                  </div>

                                  <div
                                    className={
                                      'FormBox VATCodeField' +
                                      (vatLoading ? ' DisableVATField' : '') +
                                      (formik.errors.freeTrialVatCode
                                        ? ' ErrorFB'
                                        : '')
                                    }
                                  >
                                    <div className="FormLabel">
                                      <label htmlFor="freeTrialVatCode">
                                        VAT number - optional
                                      </label>
                                    </div>
                                    <div className="FormField">
                                      {vatLoading && <Spinner />}
                                      <input
                                        id="freeTrialVatCode"
                                        type="text"
                                        className="EditBox"
                                        onChange={(e) => {
                                          formik.setFieldValue(
                                            'freeTrialVatCode',
                                            e.target.value
                                          );
                                        }}
                                        value={formik.values.freeTrialVatCode || ""}
                                      />
                                      <div
                                        className={
                                          "Btn BtnOrange VATValidateBtn" +
                                          (formik.values.isVatVerified ? " VATValidated" : "")
                                        }
                                        onClick={() =>
                                          validateVATCode(
                                            formik.values,
                                            formik.setFieldError,
                                            formik.setFieldValue
                                          )
                                        }
                                      >
                                        {formik.values.isVatVerified ? "Validated" : "Validate"}
                                      </div>
                                      {formik.errors.freeTrialVatCode && (
                                        <p className="ErrorTxt">
                                          {
                                            (formik as any).errors
                                              .freeTrialVatCode
                                          }
                                        </p>
                                      )}
                                    </div>
                                  </div>

                                  {vatAddressDetails && (
                                    <div className="VATAddressDetails">
                                      <div className="VATInfo">
                                        <h4 className="VATSchoolName">
                                          {vatAddressDetails.VATSchoolName}
                                        </h4>
                                        <p className="VATSchoolAddress">
                                          {vatAddressDetails.VATSchoolAddress}
                                        </p>
                                      </div>

                                      <div className="VATActionBtns">
                                        <div
                                          className="Btn BtnRed VATClearBtn"
                                          onClick={() =>
                                            clearVATCode(formik.setFieldValue)
                                          }
                                        >
                                          Clear
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}

                              {(formik.values.selectedLocationLabel.toLowerCase() === 'other') && (
                                <div
                                  className={
                                    'FormBox CountryListBlock' +
                                    (formik.errors.otherCountries
                                      ? ' ErrorFB'
                                      : '')
                                  }
                                >
                                  <div className="FormLabel">
                                    <label htmlFor="CountryListOther">
                                      Country
                                    </label>
                                  </div>
                                  <div className="FormField">
                                    <Dropdown
                                      id={'CountryListOther'}
                                      options={formik.values.otherCountries}
                                      value={formik.values.selectedOtherCountry}
                                      onChange={(e: SelectOption) =>
                                        formik.setFieldValue(
                                          'selectedOtherCountry',
                                          e
                                        )
                                      }
                                      customClassName="EditSingleSelect"
                                      customNoOptionsMessage="No options"
                                      placeholder={'Please select'}
                                      roundedRectangle={true}
                                    />
                                    {formik.errors.otherCountries && (
                                      <p className="ErrorTxt">
                                        {(formik as any).errors.otherCountries}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        {formik.values.ageRangeArr &&
                          formik.values.ageRangeArr.length > 0 && (
                            <div
                              className={
                                'FormBox' +
                                (formik.errors.selectedAgeRange
                                  ? ' ErrorField'
                                  : '')
                              }
                            >
                              <div className="FormLabel">
                                <label htmlFor="ageRangeArr">Age range</label>
                              </div>
                              <div className="FormField">
                                <Dropdown
                                  id={'ageRangeArr'}
                                  options={formik.values.ageRangeArr}
                                  value={formik.values.selectedAgeRange}
                                  onChange={(e: SelectOption) =>
                                    formik.setFieldValue('selectedAgeRange', e)
                                  }
                                  customClassName="EditSingleSelect"
                                  customNoOptionsMessage="No options"
                                  placeholder={'Please select'}
                                  roundedRectangle={true}
                                />
                                <ErrorM name={'selectedAgeRange'} />
                              </div>
                            </div>
                          )}
                        {formik.values.subjectArr &&
                          formik.values.subjectArr.length > 0 && (
                            <div
                              className={
                                'FormBox' +
                                (formik.errors.selectedSubject
                                  ? ' ErrorField'
                                  : '')
                              }
                            >
                              <div className="FormLabel">
                                <label htmlFor="subjectArr">Subject</label>
                              </div>
                              <div className="FormField">
                                <Dropdown
                                  id={'subjectArr'}
                                  options={formik.values.subjectArr}
                                  value={formik.values.selectedSubject}
                                  onChange={(e: SelectOption) =>
                                    formik.setFieldValue('selectedSubject', e)
                                  }
                                  customClassName="EditSingleSelect"
                                  customNoOptionsMessage="No options"
                                  placeholder={'Please select'}
                                  roundedRectangle={true}
                                />
                                <ErrorM name={'selectedSubject'} />
                              </div>
                            </div>
                          )}
                        <div className="FormBox">
                          <div className="FormLabel">Pupils on roll</div>
                          <div className="FormField">
                            <div className="PupilsNoSlider">
                              <div className="LeftVal">0</div>
                              <div className="MiddleSlider">
                                <Slider
                                  handleRender={(renderProps) => (
                                    <div {...renderProps.props}>
                                      <div
                                        className="PSTooltip"
                                        data-slider-tooltip={
                                          renderProps.props['aria-valuenow']
                                        }
                                      >
                                        {renderProps.props['aria-valuenow']}
                                      </div>
                                    </div>
                                  )}
                                  max={1000}
                                  onChange={(e) =>
                                    formik.setFieldValue('pupilsOnRoll', e)
                                  }
                                  value={formik.values.pupilsOnRoll}
                                  step={1}
                                />
                              </div>
                              <div className="RightVal">1000+</div>
                            </div>
                            <ErrorM name={'pupilsOnRoll'} />
                          </div>
                        </div>
                        <div className="LineSpacer2"></div>

                        <button className="Btn BtnOrange" type="submit">
                          Submit
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
